import { type Locale } from '../locales/constants';

const MS_PER_MINUTE = 1000 * 60;
const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY = 1000 * 60 * 60 * 24;
const MS_PER_MONTH = MS_PER_DAY * 30;
const MS_PER_YEAR = MS_PER_DAY * 365;

export const createRelativeDateFormatter = (
    locale: Locale,
    options?: Intl.RelativeTimeFormatOptions
): ((value: number, unit: Intl.RelativeTimeFormatUnit) => string) => {
    const rtf = new Intl.RelativeTimeFormat(locale, options);

    return function format(value: number, unit: Intl.RelativeTimeFormatUnit) {
        return rtf.format(value, unit);
    };
};

export const formatRelativeFutureTime = ({
    formatFn,
    date,
}: {
    formatFn: (date: number, unit: Intl.RelativeTimeFormatUnit) => string;
    date: string | number;
}): string => {
    const elapsed = new Date(date).getTime() - new Date().getTime();

    if (elapsed <= 0) {
        return formatFn(0, 'minutes');
    }

    if (elapsed >= MS_PER_YEAR) {
        return formatFn(Math.round(elapsed / MS_PER_YEAR), 'year');
    }

    if (elapsed >= MS_PER_MONTH) {
        return formatFn(Math.round(elapsed / MS_PER_MONTH), 'month');
    }

    if (elapsed >= MS_PER_DAY) {
        return formatFn(Math.round(elapsed / MS_PER_DAY), 'day');
    }

    if (elapsed >= MS_PER_HOUR) {
        return formatFn(Math.round(elapsed / MS_PER_HOUR), 'hour');
    }

    if (elapsed >= MS_PER_MINUTE) {
        return formatFn(Math.round(elapsed / MS_PER_MINUTE), 'minute');
    }

    return formatFn(0, 'minutes');
};

export const formatRelativePastTime = ({
    formatFn,
    date,
}: {
    formatFn: (date: number, unit: Intl.RelativeTimeFormatUnit) => string;
    date: string | number;
}): string => {
    const elapsed = new Date(date).getTime() - new Date().getTime();

    if (elapsed >= 0) {
        return formatFn(-0, 'minutes');
    }

    if (Math.abs(elapsed) >= MS_PER_YEAR) {
        return formatFn(Math.round(elapsed / MS_PER_YEAR), 'year');
    }

    if (Math.abs(elapsed) >= MS_PER_MONTH) {
        return formatFn(Math.round(elapsed / MS_PER_MONTH), 'month');
    }

    if (Math.abs(elapsed) >= MS_PER_DAY) {
        return formatFn(Math.round(elapsed / MS_PER_DAY), 'day');
    }

    if (Math.abs(elapsed) >= MS_PER_HOUR) {
        return formatFn(Math.round(elapsed / MS_PER_HOUR), 'hour');
    }

    if (Math.abs(elapsed) >= MS_PER_MINUTE) {
        return formatFn(Math.round(elapsed / MS_PER_MINUTE), 'minute');
    }

    return formatFn(-0, 'minutes');
};
